import React from 'react'
import { AiOutlineInfoCircle, AiOutlineWarning, AiOutlineCheckCircle } from 'react-icons/ai'
import { MdErrorOutline } from 'react-icons/md'

/**
 * Render an inline banner with contextual styling.
 *
 * @param {Object} props - Component props.
 * @param {'warning'|'info'|'error'|'success'} [props.type='warning'] - Visual style of the banner.
 * @param {string} props.message - Text content to display inside the banner.
 * @param {string} [props.className] - Optional additional class names.
 * @returns {JSX.Element|null} The styled banner or null if no message provided.
 */
function Banner({ type = 'warning', message, className = '' }) {
  if (!message) return null;

  /**
   * Map banner `type` to Tailwind utility classes.
   *
   * @param {'warning'|'info'|'error'|'success'} bannerType
   * @returns {string} Tailwind class string
   */
  const getClassesForType = (bannerType) => {
    switch (bannerType) {
      case 'info':
        return 'bg-blue-50 text-blue-800 border border-blue-200';
      case 'error':
        return 'bg-red-50 text-red-800 border border-red-200';
      case 'success':
        return 'bg-green-50 text-green-800 border border-green-200';
      case 'warning':
      default:
        return 'bg-yellow-50 text-yellow-800 border border-yellow-200';
    }
  };

  /**
   * Map banner `type` to an icon element.
   *
   * @param {'warning'|'info'|'error'|'success'} bannerType
   * @returns {JSX.Element} Icon element sized and colored via currentColor
   */
  const getIconForType = (bannerType) => {
    const commonProps = { size: 24, className: 'shrink-0' };
    switch (bannerType) {
      case 'info':
        return <AiOutlineInfoCircle {...commonProps} />;
      case 'error':
        return <MdErrorOutline {...commonProps} />;
      case 'success':
        return <AiOutlineCheckCircle {...commonProps} />;
      case 'warning':
      default:
        return <AiOutlineWarning {...commonProps} />;
    }
  };

  return (
    <div className={`rounded-md p-4 ${getClassesForType(type)} ${className}`}>
      <div className="flex items-center gap-2">
        <span aria-hidden="true" className="">{getIconForType(type)}</span>
        <p className="text-sm leading-5">{message}</p>
      </div>
    </div>
  )
}

export default Banner